import React, { useState, useRef, useEffect } from "react";
import { Sun, Moon, Monitor, ChevronDown, Check } from "lucide-react";

export type ThemePreference = "auto" | "light" | "dark";

export interface ThemeSelectorProps {
  currentTheme: string; // "auto" | "light" | "dark"
  onThemeChange: (theme: ThemePreference) => void;
  variant?: "dropdown" | "pills" | "buttons";
  className?: string;
  idPrefix?: string;
}

export function ThemeSelector({
  currentTheme,
  onThemeChange,
  variant = "dropdown",
  className = "",
  idPrefix = "theme-selector"
}: ThemeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeTheme: ThemePreference = 
    currentTheme === "dark" || currentTheme === "light" || currentTheme === "auto"
      ? currentTheme
      : "auto";

  // Close dropdown on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const options: { id: ThemePreference; label: string; icon: React.ReactNode }[] = [
    { id: "auto", label: "Auto", icon: <Monitor className="w-4 h-4 shrink-0" /> },
    { id: "light", label: "Light", icon: <Sun className="w-4 h-4 shrink-0" /> },
    { id: "dark", label: "Dark", icon: <Moon className="w-4 h-4 shrink-0" /> }
  ];

  if (variant === "dropdown") {
    const currentOption = options.find((o) => o.id === activeTheme) || options[0];

    return (
      <div className={`relative inline-block text-left ${className}`} ref={dropdownRef}>
        <button
          type="button"
          id={`${idPrefix}-dropdown-btn`}
          onClick={() => setIsOpen(!isOpen)}
          aria-haspopup="true"
          aria-expanded={isOpen}
          aria-label={`Current theme: ${currentOption.label}. Click to switch theme.`}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200/80 dark:border-slate-700/80 hover:bg-slate-200/80 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
        >
          <span className="text-slate-500 dark:text-slate-400">{currentOption.icon}</span>
          <span className="hidden sm:inline-block capitalize">{currentOption.label}</span>
          <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`} />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-1.5 w-36 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-lg ring-1 ring-black/5 dark:ring-white/10 z-50 p-1 space-y-0.5 font-sans">
            <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 border-b border-slate-100 dark:border-slate-800/80 mb-0.5">
              Theme Mode
            </div>
            {options.map((option) => {
              const isSelected = activeTheme === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  id={`${idPrefix}-opt-${option.id}`}
                  onClick={() => {
                    onThemeChange(option.id);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 text-xs font-medium rounded-lg transition cursor-pointer ${
                    isSelected
                      ? "bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 font-bold"
                      : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={isSelected ? "text-indigo-600 dark:text-indigo-400" : "text-slate-400"}>
                      {option.icon}
                    </span>
                    <span>{option.label}</span>
                  </div>
                  {isSelected && <Check className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (variant === "pills") {
    return (
      <div className={`flex items-center bg-slate-100 dark:bg-slate-800/80 p-1 rounded-xl border border-slate-200/80 dark:border-slate-700/80 gap-1 ${className}`}>
        {options.map((option) => {
          const isSelected = activeTheme === option.id;
          return (
            <button
              key={option.id}
              type="button"
              id={`${idPrefix}-pill-${option.id}`}
              onClick={() => onThemeChange(option.id)}
              aria-pressed={isSelected}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 text-xs font-semibold rounded-lg transition cursor-pointer select-none ${
                isSelected
                  ? "bg-indigo-600 text-white font-bold shadow-xs"
                  : "text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-700/60 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              <span className={isSelected ? "text-white" : "text-slate-400"}>
                {option.icon}
              </span>
              <span className="capitalize">{option.label}</span>
            </button>
          );
        })}
      </div>
    );
  }

  // "buttons" variant for modal cards
  return (
    <div className={`grid grid-cols-3 gap-2.5 ${className}`}>
      {options.map((option) => {
        const isSelected = activeTheme === option.id;
        return (
          <button
            key={option.id}
            type="button"
            id={`${idPrefix}-btn-${option.id}`}
            onClick={() => onThemeChange(option.id)}
            aria-pressed={isSelected}
            className={`p-3 rounded-xl border text-left transition flex items-center justify-between cursor-pointer ${
              isSelected
                ? "bg-indigo-600 border-indigo-600 text-white font-extrabold shadow-sm"
                : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300 dark:bg-slate-800/80 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-750"
            }`}
          >
            <div className="flex items-center gap-2">
              <span className={isSelected ? "text-white" : "text-slate-400 dark:text-slate-400"}>
                {option.icon}
              </span>
              <span className="text-xs font-semibold capitalize">{option.label}</span>
            </div>
            {isSelected && <span className="text-xs text-white">●</span>}
          </button>
        );
      })}
    </div>
  );
}
